
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useTimeAwareBackground } from './useTimeAwareBackground';
import { useTimeAwareUnsplashBackground } from './useTimeAwareUnsplashBackground';


// Registered users: 'gradient' or 'image'. Guests: always 'image'.
type Preference = 'image' | 'gradient';

// Returns preference from profile or 'image'
export const getUserBackgroundPreference = (profile: any, authenticated: boolean): Preference => {
  if (authenticated) {
    return (profile?.background_preference as Preference) || 'image';
  }
  return 'image';
};

// Update preference in DB if authenticated
export const setUserBackgroundPreference = async (
  pref: Preference,
  user: any,
  authenticated: boolean
) => {
  if (authenticated && user?.id) {
    await supabase
      .from('profiles')
      .update({ background_preference: pref })
      .eq('id', user.id);
    window.dispatchEvent(new Event('backgroundPreferenceChanged'));
  }
};

// Hybrid: use Unsplash photo by default, gradient as fallback or if user chooses
export const usePreferredBackground = () => {
  const { user, profile } = useAuth();
  const authenticated = Boolean(user && profile);
  const [preference, setPreference] = useState<Preference>(getUserBackgroundPreference(profile, authenticated));
  const {
    backgroundInfo: imageBg,
    isLoading: isImageLoading,
    error: imageError
  } = useTimeAwareUnsplashBackground();
  const {
    backgroundInfo: gradientBg,
    isLoading: isGradientLoading,
    error: gradientError,
  } = useTimeAwareBackground();
  

  // Listen to profile changes (preference) and always show 'image' if guest
  useEffect(() => {
    setPreference(getUserBackgroundPreference(profile, authenticated));
    // Listen for preference changes via window event (when saved)
    const handler = () => {
      setPreference(getUserBackgroundPreference(profile, authenticated));
    };
    window.addEventListener('backgroundPreferenceChanged', handler);
    return () => window.removeEventListener('backgroundPreferenceChanged', handler);
  }, [profile, authenticated]);


  if (preference === 'gradient') {
    return { backgroundInfo: gradientBg, isLoading: isGradientLoading, error: gradientError };
  }
  // If pref is 'image' but no image or error, fallback
  if (imageBg && !isImageLoading && !imageError) {
    return { backgroundInfo: imageBg, isLoading: false, error: null };
  }
  // Else fallback to gradient (even if loading/error!)
  return { backgroundInfo: gradientBg, isLoading: isGradientLoading, error: imageError || gradientError };
};
